/**
 * One search in the Quality tabs bar (case round, finding 7): an item or
 * case by number, customer, order or lot across Triage, Cases, Routed away
 * and Done, from the lists the shell already loads. Results drop down under
 * the box; Enter or a click opens one, Escape closes the list.
 */
import { useId, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { DoorInvestigationRead, DoorThreadRead } from '../../services/qcpInvestigations';
import { quickSearch } from './quickSearch';
import { fromTab } from './qualityWorkspaceView';
import { trackEvent } from '../../services/portalTracking';
import styles from './QualityTriageShell.module.css';

export function QualitySearch({
  threads,
  investigations,
  userName,
}: {
  threads: DoorThreadRead[];
  investigations: DoorInvestigationRead[];
  userName: string | null;
}) {
  const navigate = useNavigate();
  const listId = useId();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const hits = quickSearch(query, threads, investigations, userName);
  const showList = open && query.trim().length >= 2;

  const go = (i: number) => {
    const hit = hits[i];
    if (!hit) return;
    setOpen(false);
    setQuery('');
    // Which hit, never what was typed.
    trackEvent('action', 'qcp_search_open', { item_no: hit.no, rank: i, hits: hits.length, where: hit.origin });
    navigate(hit.path, fromTab(hit.origin));
  };

  return (
    <div className={styles.search}>
      <input
        type="search"
        className={styles.searchBox}
        value={query}
        placeholder="Find an item: number, customer, order, lot"
        aria-label="Find an item or case"
        title="Find an item or case: number, customer, order or lot"
        data-filled={query ? true : undefined}
        role="searchbox"
        data-shortcut="/"
        aria-controls={listId}
        aria-expanded={showList}
        onChange={(e) => {
          setQuery(e.target.value);
          setActive(0);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => window.setTimeout(() => setOpen(false), 150)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') setOpen(false);
          else if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActive((a) => Math.min(a + 1, Math.max(hits.length - 1, 0)));
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActive((a) => Math.max(a - 1, 0));
          } else if (e.key === 'Enter') {
            e.preventDefault();
            go(active);
          }
        }}
      />
      {showList && (
        <ul className={styles.searchList} id={listId} role="listbox" aria-label="Matching items">
          {hits.length === 0 && <li className={styles.searchEmpty}>Nothing matches “{query.trim()}”.</li>}
          {hits.map((hit, i) => (
            <li
              key={hit.key}
              role="option"
              aria-selected={i === active}
              className={`${styles.searchHit} ${i === active ? styles.searchHitOn : ''}`}
              // mousedown, not click: the box's blur would close the list first.
              onMouseDown={(e) => {
                e.preventDefault();
                go(i);
              }}
              onMouseEnter={() => setActive(i)}
              title={`${hit.no} · ${hit.title} · ${hit.where}`}
            >
              <span className={styles.searchNo}>{hit.no}</span>
              <span className={styles.searchTitle}>{hit.title}</span>
              <span className={styles.searchWhere}>{hit.where}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
